import React from 'react';
import { useTranslation } from 'react-i18next';
import type { AwCtx } from '../../ctx';
import { card, note } from '../../ui';
import { chip, chipOn } from '../../dialog';
import { FA_TABS, FaTab, canWriteIn, isReadOnlySeat, useFaParams } from './shared';
import { CategoriesTab } from './CategoriesTab';
import { RegisterTab } from './RegisterTab';
import { DepreciationTab } from './DepreciationTab';

const TAB_ICON: Record<FaTab, string> = { categories: 'ti ti-category', register: 'ti ti-building-warehouse', depreciation: 'ti ti-chart-line' };

/**
 * Accounting › Fixed assets, as the customer asked for it (batch 9 R4, COA-H1): three tabs —
 * Asset Categories | Asset Register | Depreciation — each opening its records full screen (COA-H16).
 * The classic page (Bookkeeping/StatutoryBooks) keeps the old one-card component; this screen is the
 * workspace's own, over the same routes, so a rule the server enforces holds on both.
 * The tab lives in the URL (?fatab=), so opening and closing a window never loses it.
 */
export function FixedAssetsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const fp = useFaParams();
  const canWrite = canWriteIn(ctx);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div role="tablist" aria-label={t('aw_fa_tabs_label')} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {FA_TABS.map(k => (
          <button key={k} type="button" role="tab" aria-selected={fp.tab === k} data-fa-tab={k}
            onClick={() => fp.setTab(k)} style={fp.tab === k ? { ...chip, ...chipOn } : chip}>
            <i className={TAB_ICON[k]} aria-hidden /> {t(`aw_fa_tab_${k}`)}
          </button>
        ))}
      </div>
      {ctx.periodClosed && !isReadOnlySeat(ctx) && (
        <div style={{ ...card, ...note, padding: '9px 12px' }}><i className="ti ti-lock" aria-hidden /> {t('aw_period_closed_msg')}</div>
      )}
      {fp.tab === 'categories' && <CategoriesTab ctx={ctx} canWrite={canWrite} />}
      {fp.tab === 'register' && <RegisterTab ctx={ctx} canWrite={canWrite} />}
      {fp.tab === 'depreciation' && <DepreciationTab ctx={ctx} canWrite={canWrite} />}
    </div>
  );
}
